'use client'

import {
  dateToString,
  type IBasicUser,
  type IChatLiteMessage,
  type IChatResponse,
  IMeta,
  type IRefMessages,
  useRefRevertInfiniteScroll,
} from '@repo/sdk'
import { Spinner } from '@repo/ui'
import { cn } from '@repo/ui/lib'
import { ArrowDownIcon } from 'lucide-react'
import Image from 'next/image'
import {
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { Message } from './message-next.tsx'

interface IParsedMessage {
  children?: ReactNode
  event?: string
  message: IChatLiteMessage
  value?: string
}

const Event = ({
  data: { children, event, message, value },
  prevMessage,
  userData,
}: {
  data: IParsedMessage
  prevMessage: IChatLiteMessage
  userData: Partial<IBasicUser>
}) => {
  if (children) {
    return children
  }

  if (event === 'isNew') {
    return (
      <p className="my-2 rounded-3xl bg-gray-300 px-3 py-1 text-center text-xs text-gray-700">
        Unread messages
      </p>
    )
  }

  if (event === 'date') {
    return (
      <div className="text-center">
        <p className="my-2 rounded-3xl bg-gray-300 px-3 py-1 text-center text-xs text-gray-700">
          {value}
        </p>
      </div>
    )
  }

  return <Message message={message} prevMessage={prevMessage} userData={userData} />
}

const EmptyMessage = () => {
  const [clickCount, setClickCount] = useState(0)

  return (
    <div>
      <div className={'flex h-full w-full flex-col gap-3'}>
        <Image
          alt={'no messages'}
          height={100}
          onClick={() => {
            setClickCount(clickCount + 1)
          }}
          src={'/no-message-chat.svg'}
          width={100}
        />
        <p className={'font-semibold'}>No messages yet.</p>
        <p className={'max-w-[275px] text-center text-gray-600'}>
          Start a conversation about your shipment with us now!
        </p>
      </div>
      <div
        className={`transition-transform duration-500 ${
          clickCount > 4 ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <Image alt={'easter egg'} height={500} src={'/easter-egg.png'} width={500} />
      </div>
    </div>
  )
}

const groupMessagesByDate = (
  messages: IChatLiteMessage[],
  userData: Partial<IBasicUser>,
  parserMessages: (
    message: IChatLiteMessage,
    userData: Partial<IBasicUser>,
  ) => ReactNode | undefined,
  isNewMessageEvent?: boolean,
) => {
  return messages.reduce<IParsedMessage[]>((acc, message, index) => {
    const customMessage = parserMessages?.(message, userData)

    if (customMessage) {
      acc.push({
        children: customMessage,
        message,
      })
      return acc
    }

    acc.push({
      message,
    })

    // PostEvent
    if (
      isNewMessageEvent &&
      messages[index + 1] &&
      !message?.read_at &&
      message?.user_id_no_read === userData.id &&
      (messages[index + 1]?.read_at || messages[index + 1].user_id_no_read !== userData.id)
    ) {
      acc.push({
        event: 'isNew',
        message,
      })
    }

    const date = dateToString(message.created_at)
    const lastDate = dateToString(messages[index + 1]?.created_at)

    if ((date && lastDate && date !== lastDate) || !lastDate) {
      acc.push({
        event: 'date',
        message,
        value: dateToString(message.created_at),
      })
    }

    return acc
  }, [])
}

interface IProps {
  chatId: number
  className?: string
  fetch: (page: IMeta) => Promise<IChatResponse | undefined | void>
  isLoadingDefault?: boolean
  isNewMessageEvent?: boolean
  markMessagesAsRead: () => void
  parserMessages: (message: IChatLiteMessage) => ReactNode | undefined
  userData?: Partial<IBasicUser>
}

export const ChatNext = forwardRef(
  (
    {
      chatId,
      className = '',
      fetch,
      isLoadingDefault = false,
      isNewMessageEvent,
      markMessagesAsRead,
      parserMessages,
      userData,
    }: IProps,
    ref: ForwardedRef<IRefMessages | undefined>,
  ) => {
    const [unreadNewMessage, setUnreadNewMessage] = useState(false)
    const [isLoadingInit, setIsLoading] = useState<boolean>(true)
    const [isLoadingComponent, setIsLoadingComponent] = useState<boolean>(isLoadingDefault)

    const isLoading = isLoadingInit || isLoadingComponent
    const [page, setPage] = useState<IMeta>({
      current_page: 0,
      from: 1,
      last_page: 1,
      per_page: 30,
      to: 0,
      total: 1,
    })
    const [oldScrollHeight, setOldScrollHeight] = useState<number | undefined>(undefined)
    const [oldScrollHeightDown, setOldScrollHeightDown] = useState<number | undefined>(undefined)
    const [messages, setMessages] = useState<IChatLiteMessage[]>([])
    const refMessages =
      (ref as RefObject<IRefMessages | undefined>) ||
      // eslint-disable-next-line react-compiler/react-compiler
      useRef<IRefMessages>(undefined)
    const isPossibleFetchData = page.current_page < page.last_page && messages[0]

    useEffect(() => {
      if (!isLoading && chatId) {
        setMessages([])

        setPage({
          current_page: 0,
          from: 1,
          last_page: 1,
          per_page: 30,
          to: 0,
          total: 1,
        })
        setIsLoading(true)
        setUnreadNewMessage(false)
        setIsLoadingComponent(isLoadingDefault)

        setOldScrollHeight(undefined)
        setOldScrollHeightDown(undefined)
        reset()
      }
    }, [chatId])

    useEffect(() => {
      if (!isLoading) markMessagesAsRead()
    }, [isLoading])

    useEffect(() => {
      if (refMessages.current && messages[0] && oldScrollHeight !== undefined) {
        endFetch(oldScrollHeight)
        refMessages.current.setOldScrollHeight(undefined)
      }
    }, [messages])

    useEffect(() => {
      if (refBox.current?.scrollHeight && messages[0] && oldScrollHeightDown !== undefined) {
        refBox.current.scrollTop =
          refBox.current.scrollTop + refBox.current.scrollHeight - oldScrollHeightDown
        refMessages.current?.setOldScrollHeightDown(undefined)
      }
    }, [messages])

    useImperativeHandle(
      refMessages,
      () => ({
        getMessages() {
          return messages
        },
        getPage() {
          return page
        },
        getUnreadNewMessage() {
          return unreadNewMessage
        },
        setIsLoading(value) {
          setIsLoadingComponent(value)
        },
        setMessages(newMessages: IChatLiteMessage[]) {
          refMessages.current?.setOldScrollHeightDown(refBox.current?.scrollHeight)
          if (refBox.current) {
            if (
              refBox.current.offsetHeight + refBox.current.scrollTop + 20 <
              refBox.current.scrollHeight
            ) {
              setUnreadNewMessage(true)
            }
          }

          setMessages(newMessages)
          if (isLoading) {
            setIsLoading(false)
          }
        },
        setOldMessages(newMessages: IChatLiteMessage[]) {
          setMessages(newMessages)
          if (isLoading) {
            setIsLoading(false)
          }
        },
        setOldScrollHeight(oldHeight) {
          setOldScrollHeight(oldHeight)
        },
        setOldScrollHeightDown(oldHeight) {
          setOldScrollHeightDown(oldHeight)
        },
        setPage(newPage) {
          setPage(newPage)
        },
        setUnreadNewMessage(value) {
          setUnreadNewMessage(value)
        },
        ...refMessages?.current,
      }),
      [messages, unreadNewMessage, chatId],
    )

    const fetchData = async () => {
      if (
        refMessages.current &&
        refMessages.current.getPage().current_page < refMessages.current.getPage().last_page
      ) {
        const oldMessage = refMessages.current.getMessages()
        const oldMeta = refMessages.current.getPage()
        const quantityNewMessage = oldMessage.length - oldMeta.to
        const page =
          // eslint-disable-next-line no-bitwise
          oldMeta.current_page + 1 + ~~(quantityNewMessage / oldMeta.per_page)
        const response = await fetch({ ...oldMeta, current_page: page })
        if (response?.data) {
          if (oldMeta.current_page !== response?.meta.current_page) {
            refMessages.current.setPage(response?.meta)

            refMessages.current.setOldMessages([
              ...oldMessage,
              ...response.data.slice(quantityNewMessage % oldMeta.per_page),
            ])
          }
        } else {
          setIsLoading(false)
        }
      }
    }

    const fetchMessages = async (oldScrollHeight: number) => {
      if (refMessages.current) {
        refMessages.current.setOldScrollHeight(oldScrollHeight)
        await fetchData()
      }
    }

    const hiddenUnreadNewMessage = async () => {
      if (
        refMessages.current?.getUnreadNewMessage() &&
        refBox.current &&
        refBox.current.offsetHeight + refBox.current.scrollTop < refBox.current.scrollHeight + 20
      ) {
        refMessages.current.setUnreadNewMessage(false)
      }
    }

    const { endFetch, refBox, reset } = useRefRevertInfiniteScroll(
      fetchMessages,
      hiddenUnreadNewMessage,
      messages,
      isLoadingComponent,
    )

    const groupedMessages =
      userData && groupMessagesByDate(messages, userData, parserMessages, isNewMessageEvent)

    return (
      <div
        className={cn(
          className,
          'relative flex h-full flex-col justify-between gap-4 overflow-y-auto bg-white pt-2',
        )}
        ref={refBox}
      >
        {unreadNewMessage && (
          <div
            className={
              'fixed bottom-[90px] right-[150px] z-[999] cursor-pointer rounded-[99px] bg-gray-600 px-6 py-1 text-white'
            }
          >
            <p className={'mb-2'}>
              <div className={'flex items-center'}>
                <ArrowDownIcon style={{ marginRight: '8px', marginTop: '2px' }} />
                new message(s)
              </div>
            </p>
          </div>
        )}
        {((isLoading && !messages[0]) || isPossibleFetchData || !userData) && (
          <div className={'h-10 text-center'}>
            <Spinner className={'color-primary h-[30px] w-[30px]'} />
          </div>
        )}
        {!isLoading && !messages[0] && <EmptyMessage />}
        {!isLoading && messages[0] && userData && (
          <div className="flex h-auto flex-col-reverse space-y-1 space-y-reverse px-4">
            {groupedMessages?.map((data, index) => (
              <div className="flex h-auto flex-col space-y-1" key={`${index}-${data.message.id}`}>
                <Event
                  data={data}
                  prevMessage={groupedMessages[index - 1]?.message}
                  userData={userData}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  },
)

ChatNext.displayName = 'ChatNext'
